/**
 * 姿势库目录（P0-6a）：977 姿势的分类 / 搜索纯逻辑。
 *
 * 数据来源是运行时 fetch 的 `/poses/catalog.json`（由 scripts/gen_pose_catalog.py
 * 从 poses_full.csv 生成）。本模块只做**纯函数**（过滤/分组/URL 拼接），不碰 fetch、
 * 不碰 React——与 cameraMath/inspector 同一分层原则：可计算的全放这里离线测，
 * UI 只负责渲染与转发。
 *
 * ⚠️ 名称/分类来源合规见技术债 D-34（发布前需替换为我方自命名）。
 */
import { parsePoseFile, type BoneMap } from '../../model/pose';

export interface PoseEntry {
  id: string;
  category: string;
  name: string;
  description: string;
}

/** "全部"分类的哨兵值（UI 顶部分类条用它表示不过滤） */
export const ALL_CATEGORY = '全部';

/** 缩略图 URL（与 bones 同 id，扩展名 webp） */
export function poseThumbUrl(id: string): string {
  return `/poses/thumbs/${id}.webp`;
}

/** 骨骼数据 URL（applyBoneMap 的输入） */
export function poseBoneUrl(id: string): string {
  return `/poses/bones/${id}.json`;
}

/** 去重分类，保持首次出现顺序（catalog 已按 CSV 行序，稳定可预期） */
export function categoriesOf(entries: readonly PoseEntry[]): string[] {
  const out: string[] = [];
  for (const e of entries) {
    if (!out.includes(e.category)) {
      out.push(e.category);
    }
  }
  return out;
}

/** 每个分类的姿势数（供分类条上的计数徽标） */
export function countByCategory(
  entries: readonly PoseEntry[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of entries) {
    counts[e.category] = (counts[e.category] ?? 0) + 1;
  }
  return counts;
}

export interface PoseFilter {
  /** 分类精确匹配；ALL_CATEGORY 或空表示不限 */
  category?: string;
  /** 搜索词，匹配 名称/分类/描述 任一（大小写不敏感、去首尾空格） */
  query?: string;
}

/**
 * 过滤 + 轻排序：先按分类，再按搜索词；命中名称的排在只命中描述的前面。
 * 纯函数，不改动入参。空/全空格 query 视为不过滤。
 */
export function filterPoses(
  entries: readonly PoseEntry[],
  filter: PoseFilter,
): PoseEntry[] {
  const cat = filter.category;
  const byCat =
    cat === undefined || cat === ALL_CATEGORY || cat === ''
      ? entries.slice()
      : entries.filter((e) => e.category === cat);

  const q = (filter.query ?? '').trim().toLowerCase();
  if (q === '') {
    return byCat;
  }
  const scored: Array<{ e: PoseEntry; rank: number }> = [];
  for (const e of byCat) {
    const name = e.name.toLowerCase();
    const category = e.category.toLowerCase();
    const desc = e.description.toLowerCase();
    let rank = -1;
    if (name.includes(q)) {
      rank = 0;
    } else if (category.includes(q)) {
      rank = 1;
    } else if (desc.includes(q)) {
      rank = 2;
    }
    if (rank >= 0) {
      scored.push({ e, rank });
    }
  }
  // rank 升序；同 rank 保持原顺序（Array.sort 在现代引擎稳定，但显式带索引兜底）
  return scored
    .map((s, i) => ({ s, i }))
    .sort((a, b) => a.s.rank - b.s.rank || a.i - b.i)
    .map((x) => x.s.e);
}

/** 按分类分组（供分组渲染；保持 categoriesOf 的顺序） */
export function groupByCategory(
  entries: readonly PoseEntry[],
): Array<{ category: string; poses: PoseEntry[] }> {
  const groups = new Map<string, PoseEntry[]>();
  for (const e of entries) {
    const arr = groups.get(e.category);
    if (arr === undefined) {
      groups.set(e.category, [e]);
    } else {
      arr.push(e);
    }
  }
  return [...groups.entries()].map(([category, poses]) => ({ category, poses }));
}

/** catalog 结构校验（fetch 后先过一遍，坏数据不喂给 UI）：非空、字段齐全、id 唯一 */
export function isValidCatalog(data: unknown): data is PoseEntry[] {
  if (!Array.isArray(data) || data.length === 0) {
    return false;
  }
  const seen = new Set<string>();
  for (const item of data) {
    if (typeof item !== 'object' || item === null) {
      return false;
    }
    const e = item as Record<string, unknown>;
    if (
      typeof e.id !== 'string' ||
      typeof e.category !== 'string' ||
      typeof e.name !== 'string' ||
      e.id === '' ||
      e.category === '' ||
      e.name === ''
    ) {
      return false;
    }
    if (seen.has(e.id)) {
      return false;
    }
    seen.add(e.id);
  }
  return true;
}

/**
 * 带模块级缓存的 catalog 加载器（只 fetch 一次，多处复用）。
 * 放这里而非纯函数区：它依赖运行时 fetch，不进单测（纯函数部分已充分覆盖）。
 * 失败不缓存（下次重试），返回 null 供 UI 显式处理错误态。
 */
let catalogPromise: Promise<PoseEntry[] | null> | null = null;

export function loadPoseCatalog(): Promise<PoseEntry[] | null> {
  if (catalogPromise === null) {
    catalogPromise = fetch('/poses/catalog.json')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => (isValidCatalog(data) ? data : null))
      .catch(() => {
        catalogPromise = null; // 失败不缓存，下次打开可重试
        return null;
      });
  }
  return catalogPromise;
}

/**
 * 预设姿势骨骼数据缓存加载器（P0-6c 关节编辑需要：合成 = 预设骨图 ⊕ controls ⊕ overrides）。
 * 同一 id 只 fetch 一次（连续拖滑杆会反复取同一预设，必须缓存）。失败不缓存、返回空图。
 */
const boneCache = new Map<string, Promise<BoneMap>>();

export function loadPoseBones(id: string): Promise<BoneMap> {
  let p = boneCache.get(id);
  if (p === undefined) {
    p = fetch(poseBoneUrl(id))
      .then((res) => (res.ok ? res.json() : {}))
      .then((json: unknown) => parsePoseFile(json))
      .catch(() => {
        boneCache.delete(id); // 失败不缓存，下次可重试
        return {} as BoneMap;
      });
    boneCache.set(id, p);
  }
  return p;
}
